/**
 * Validation schemas for Product Library System
 * Using Zod for runtime validation and type safety
 */

import { z } from 'zod';

// IO Connection Schema
export const IOConnectionSchema = z.object({
  type: z.string().min(1),
  name: z.string().min(1),
  connector: z.string().min(1),
});

// Dimension Schema
export const DimensionSchema = z.object({
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  depth: z.number().positive().optional(),
  unit: z.enum(['in', 'inches', 'mm', 'cm']),
});

// Weight Schema
export const WeightSchema = z.object({
  value: z.number().positive().optional(),
  unit: z.enum(['lb', 'lbs', 'kg', 'oz', 'g']),
});

// Create Product Library Item Schema
export const CreateProductLibraryItemSchema = z
  .object({
    category: z.string().min(1),
    subcategory: z.string().optional().nullable(),
    brand: z.string().min(1).max(100),
    model: z.string().min(1).max(100),
    part_number: z.string().max(100).optional().nullable(),
    description: z.string().max(500).optional().nullable(),
    specifications: z.record(z.any()).optional(),

    // Lighting-specific
    wattage: z.number().positive().optional(),
    lumens: z.number().positive().optional(),
    color_temp: z
      .string()
      .regex(/^\d{4}K$/)
      .optional()
      .nullable(), // e.g., "3000K"
    beam_angle: z.number().min(0).max(360).optional(),
    dimming_type: z.string().optional(),

    // AV-specific
    inputs: z.array(IOConnectionSchema).optional(),
    outputs: z.array(IOConnectionSchema).optional(),
    power_consumption: z.number().positive().optional(),
    mounting_type: z.string().optional(),

    // Common fields
    dimensions: DimensionSchema.optional(),
    weight: WeightSchema.optional(),
    price: z.number().positive().max(999999.99).optional(),
    currency: z.string().length(3).default('USD').optional(),
    image_url: z.string().url().optional().nullable(),
    image_source: z.string().optional(),
    spec_sheet_url: z.string().url().optional(),
    manufacturer_url: z.string().url().optional(),
    is_public: z.boolean().default(false).optional(),

    // Source tracking (from AI product creation)
    source: z.string().optional(),
    creation_method: z.string().optional(),
    ai_confidence_score: z.number().optional(),

    // Form-specific flat fields (mapped from form to nested)
    width: z.number().positive().optional(),
    height: z.number().positive().optional(),
    depth: z.number().positive().optional(),
    dimension_unit: z.string().optional(),
    weight_value: z.number().positive().optional(),
    weight_unit: z.string().optional(),

    // Database columns for dimensions in mm
    width_mm: z.number().int().positive().optional(),
    height_mm: z.number().int().positive().optional(),
    depth_mm: z.number().int().positive().optional(),
    weight_kg: z.number().positive().optional(),

    // Custom icon fields (overrides category icon)
    custom_icon_svg: z.string().optional().nullable(),
    custom_icon_abbreviation: z.string().max(10).optional().nullable(),
  })
  .passthrough();

// Update Product Library Item Schema
export const UpdateProductLibraryItemSchema = CreateProductLibraryItemSchema.partial();

// Low Voltage Product Schema
export const LVProductSchema = z.object({
  id: z.string().uuid(),
  floor_plan_id: z.string().uuid(),
  product_id: z.string().uuid().optional(),
  category: z.string().min(1),
  subcategory: z.string().optional().nullable(),
  name: z.string().min(1).max(100),
  x_position: z.number(),
  y_position: z.number(),
  rotation: z.number().min(0).max(360).default(0),
  scale: z.number().positive().max(10).default(1),
  parent_group_id: z.string().uuid().optional(),
  room_id: z.string().uuid().nullable().optional(),
  ip_address: z.string().ip().optional(),
  mac_address: z
    .string()
    .regex(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/)
    .optional(),
  serial_number: z.string().optional(),
  installation_notes: z.string().max(1000).optional(),
  metadata: z.record(z.any()).default({}),
  is_locked: z.boolean().default(false),
});

// Create Low Voltage Product Schema
export const CreateLVProductSchema = z.object({
  floor_plan_id: z.string().uuid(),
  product_id: z.string().uuid().nullable().optional(),
  category: z.string().min(1),
  subcategory: z.string().optional().nullable(),
  name: z.string().min(1).max(100),
  x_position: z.number(),
  y_position: z.number(),
  rotation: z.number().min(0).max(360).optional(),
  scale: z.number().positive().max(10).optional(),
  parent_group_id: z.string().uuid().optional(),
  room_id: z.string().uuid().nullable().optional(),
  ip_address: z.string().ip().optional(),
  mac_address: z
    .string()
    .regex(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/)
    .optional(),
  serial_number: z.string().optional(),
  installation_notes: z.string().max(1000).optional(),
  metadata: z.record(z.any()).optional(),
  // Product image callout fields
  show_product_image: z.boolean().optional(),
  product_image_x: z.number().optional(),
  product_image_y: z.number().optional(),
  product_image_width: z.number().positive().optional(),
  product_image_height: z.number().positive().optional(),
});

// Update Low Voltage Product Schema
export const UpdateLVProductSchema = CreateLVProductSchema.partial()
  .omit({
    floor_plan_id: true,
  })
  .extend({
    product_id: z.string().uuid().nullable().optional(),
    // Product image callout fields
    show_product_image: z.boolean().optional(),
    product_image_x: z.number().optional(),
    product_image_y: z.number().optional(),
    product_image_width: z.number().positive().optional(),
    product_image_height: z.number().positive().optional(),
  });

// Legacy export names for backward compatibility (will be removed)
export const CreateAVProductSchema = CreateLVProductSchema;
export const UpdateAVProductSchema = UpdateLVProductSchema;

// Product Connection Schema
export const ProductConnectionSchema = z.object({
  id: z.string().uuid(),
  floor_plan_id: z.string().uuid(),
  source_product_id: z.string().uuid(),
  source_output: z.string().min(1),
  target_product_id: z.string().uuid(),
  target_input: z.string().min(1),
  connection_type: z.enum(['audio', 'video', 'network', 'control', 'power']).optional(),
  cable_type: z.string().optional(),
  cable_length: z.number().positive().optional(),
  cable_unit: z.enum(['ft', 'm']).default('ft').optional(),
  path_points: z
    .array(
      z.object({
        x: z.number(),
        y: z.number(),
      }),
    )
    .default([]),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  notes: z.string().max(500).optional(),
});

// Create Product Connection Schema
export const CreateProductConnectionSchema = ProductConnectionSchema.omit({
  id: true,
});

// Update Product Connection Schema
export const UpdateProductConnectionSchema = CreateProductConnectionSchema.partial().omit({
  floor_plan_id: true,
});

// Search Params Schema
export const ProductSearchParamsSchema = z.object({
  query: z.string().optional(),
  category: z.string().min(1).optional(),
  subcategory: z.string().optional(),
  brand: z.string().optional(),
  minPrice: z.number().positive().optional(),
  maxPrice: z.number().positive().optional(),
  is_public: z.boolean().optional(),
  is_verified: z.boolean().optional(),
  limit: z.number().positive().max(500).default(20),
  offset: z.number().nonnegative().default(0),
});

// Validation helper functions
export const validateProductLibraryItem = (data: unknown) => {
  return CreateProductLibraryItemSchema.parse(data);
};

export const validateLVProduct = (data: unknown) => {
  return CreateLVProductSchema.parse(data);
};

export const validateProductConnection = (data: unknown) => {
  return CreateProductConnectionSchema.parse(data);
};
